/**
 * The device's copy of the catalog (doc/12 §Offline model).
 *
 * This is not a cache - it is the UI's only read source. Every screen renders
 * from here whether or not there is a network, which is why "works offline" is a
 * property of the architecture rather than a feature we remembered to add to
 * some screens.
 *
 * Index rule: index what the UI sorts, filters or joins on. Every extra index is
 * paid for on every write, and writes happen in bulk on every sync pull.
 */
import Dexie, { type EntityTable } from 'dexie';
import type {
  Checkpoint,
  Company,
  Designer,
  EjectionType,
  GateType,
  Location,
  Material,
  MaterialGrade,
  MechanismType,
  Mould,
  NameplateTemplate,
  Project,
  RunnerType,
  Store,
  SyncMutation,
  SyncTable,
  ToolType,
} from '@mouldtrack/shared';

/** Server-managed columns that arrive with every replicated row. */
export interface LocalMeta {
  updatedAt: string;
  deletedAt?: string | null;
  /** Set while a local edit is still sitting in the outbox - drives the "pending" dot. */
  _pending?: 1 | 0;
}

export type Local<T> = T & LocalMeta;

/** A queued mutation plus the bookkeeping the engine needs to retry it safely. */
export interface OutboxEntry extends SyncMutation {
  seq?: number;
  status: 'queued' | 'sending' | 'failed';
  attempts: number;
  /** Set after a failure; the engine backs off rather than hammering a broken server. */
  lastError?: string;
  nextAttemptAt?: string;
}

/**
 * A losing edit. We keep it instead of discarding it, because silently dropping
 * someone's work is the fastest way to lose their trust in the system.
 */
export interface ConflictEntry {
  id?: number;
  table: SyncTable;
  rowId: string;
  detectedAt: string;
  /** What this device tried to write. */
  localData: Record<string, unknown>;
  /** What the server had instead. */
  serverData: Record<string, unknown>;
  resolved: 0 | 1;
}

/** Small key/value store: sync cursor, session, UI preferences. */
export interface MetaEntry {
  key: string;
  value: unknown;
}

/**
 * A scan taken on the floor, waiting to reach the server.
 *
 * Its own queue rather than a row in `outbox`, because the two are not the same
 * kind of thing. The outbox carries catalog *edits*: they conflict, they need
 * `baseUpdatedAt`, and the last writer wins. A scan is an append-only fact about
 * a moment that has already passed - it cannot conflict, it is never overwritten,
 * and it goes to a different endpoint that reports per-item results. Forcing it
 * through the outbox would mean teaching that engine a second set of rules.
 *
 * The display fields are denormalised on purpose. When someone opens the app
 * after a morning in a dead zone, "MLD-014 checked out at Mira Road" is what
 * they need to see; re-deriving that from ids would fail for exactly the rows
 * that matter most, the ones taken before a catalog pull.
 */
export interface QueuedScan {
  seq?: number;
  /** Idempotency key, minted at the moment of the scan. */
  clientScanId: string;
  assetCode: string;
  checkpointCode: string;
  scannedAt: string;
  remarks?: string;
  expectedBackBy?: string | null;
  // --- shown back to the person who took it ---
  mouldName: string | null;
  placeLabel: string;
  actionLabel: string;
  // --- bookkeeping ---
  status: 'queued' | 'sending' | 'failed';
  attempts: number;
  lastError?: string;
}

export class MouldTrackDB extends Dexie {
  // --- catalog masters ---
  companies!: EntityTable<Local<Company>, 'id'>;
  projects!: EntityTable<Local<Project>, 'id'>;
  designers!: EntityTable<Local<Designer>, 'id'>;
  toolTypes!: EntityTable<Local<ToolType>, 'id'>;
  runnerTypes!: EntityTable<Local<RunnerType>, 'id'>;
  gateTypes!: EntityTable<Local<GateType>, 'id'>;
  ejectionTypes!: EntityTable<Local<EjectionType>, 'id'>;
  materials!: EntityTable<Local<Material>, 'id'>;
  materialGrades!: EntityTable<Local<MaterialGrade>, 'id'>;
  mechanismTypes!: EntityTable<Local<MechanismType>, 'id'>;
  nameplateTemplates!: EntityTable<Local<NameplateTemplate>, 'id'>;

  // --- movement masters ---
  locations!: EntityTable<Local<Location>, 'id'>;
  stores!: EntityTable<Local<Store>, 'id'>;
  checkpoints!: EntityTable<Local<Checkpoint>, 'id'>;

  // --- the catalog itself ---
  moulds!: EntityTable<Local<Mould>, 'id'>;

  // --- engine bookkeeping ---
  outbox!: EntityTable<OutboxEntry, 'seq'>;
  scanQueue!: EntityTable<QueuedScan, 'seq'>;
  conflicts!: EntityTable<ConflictEntry, 'id'>;
  meta!: EntityTable<MetaEntry, 'key'>;

  constructor() {
    super('mouldtrack');

    // Every master shares the same shape, so they share the same index set.
    const MASTER_IDX = 'id, code, name, isActive, updatedAt, deletedAt';

    this.version(1).stores({
      companies: MASTER_IDX,
      projects: `${MASTER_IDX}, ownerCompanyId`,
      designers: MASTER_IDX,
      toolTypes: MASTER_IDX,
      runnerTypes: MASTER_IDX,
      gateTypes: MASTER_IDX,
      ejectionTypes: MASTER_IDX,
      materials: MASTER_IDX,
      materialGrades: MASTER_IDX,
      mechanismTypes: MASTER_IDX,
      nameplateTemplates: MASTER_IDX,

      locations: 'id, code, name, type, updatedAt, deletedAt',
      stores: 'id, code, name, locationId, updatedAt, deletedAt',
      checkpoints: 'id, code, name, type, locationId, storeId, updatedAt, deletedAt',

      // The catalog list filters by project/owner/toolType and sorts by code, so
      // those four carry indexes; the other dozen spec FKs are filtered in memory
      // over ~67 rows, which is faster than maintaining the indexes would be.
      moulds:
        'id, assetCode, name, status, type, isActive, projectId, ownerCompanyId, manufactureCompanyId, toolTypeId, updatedAt, deletedAt, _pending',

      outbox: '++seq, clientMutationId, table, id, status, nextAttemptAt',
      conflicts: '++id, table, rowId, resolved, detectedAt',
      meta: 'key',
    });

    // v2 adds the scan queue. Additive, so Dexie carries an existing device's
    // catalog and unsent edits across the upgrade untouched - a supervisor who
    // opens the app on Monday does not lose Friday's work to a version bump.
    this.version(2).stores({
      scanQueue: '++seq, clientScanId, assetCode, status, scannedAt',
    });
  }

  /** Wipe every replicated row - used on sign-out so a shared device leaks nothing. */
  async clearReplicatedData(): Promise<void> {
    const tables = this.tables.filter((t) => t.name !== 'meta');
    await this.transaction('rw', tables, async () => {
      await Promise.all(tables.map((t) => t.clear()));
    });
  }
}

export const db = new MouldTrackDB();

/** Dexie table name for each syncable server table. They match 1:1 by design. */
export const SYNC_TABLE_MAP = {
  companies: 'companies',
  projects: 'projects',
  designers: 'designers',
  toolTypes: 'toolTypes',
  runnerTypes: 'runnerTypes',
  gateTypes: 'gateTypes',
  ejectionTypes: 'ejectionTypes',
  materials: 'materials',
  materialGrades: 'materialGrades',
  mechanismTypes: 'mechanismTypes',
  nameplateTemplates: 'nameplateTemplates',
  locations: 'locations',
  stores: 'stores',
  checkpoints: 'checkpoints',
  moulds: 'moulds',
} as const satisfies Record<SyncTable, string>;

// --- meta helpers ---

export const META_KEYS = {
  syncCursor: 'sync.cursor',
  lastPullAt: 'sync.lastPullAt',
  lastPushAt: 'sync.lastPushAt',
  bootstrapped: 'sync.bootstrapped',
  session: 'auth.session',
} as const;

export async function getMeta<T>(key: string): Promise<T | undefined> {
  const row = await db.meta.get(key);
  return row?.value as T | undefined;
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await db.meta.put({ key, value });
}

export async function deleteMeta(key: string): Promise<void> {
  await db.meta.delete(key);
}
